import moment from 'moment-timezone';
import { SpacesState, SpacesActionTypes } from '../types/spaces';
import { ImplementedState, ImplementedActionTypes } from '../types/implemented';
import { ResourceStatus } from '../types/resource';
import { Filter, FilterOfValueType } from '../types/filters';
import { OrganizationIdOrAll } from '../types/all-organizations';
import { Sort } from '../types/sort';
import { SenseiApi } from '../utils/api';
import { Organization } from '../types/api';

import { getDefaultSortFunction } from '@densityco/ui';
import { ALL_ORGANIZATIONS } from '../types/all-organizations';

const PAGE_SIZE = 50;

function generateParams(
  filters: Array<Filter>,
  searchText: string,
  sort: Sort,
  organizationId: Organization['id'] | null,
): { [key: string]: string | number } {
  // Apply filters to the results that are returned.
  const spaceFunctionFilters = filters.filter(
    (i) => i.definitionId === 'space_function',
  ) as Array<FilterOfValueType<Array<string>>>;

  const filterToQuery = (
    definitionId: string,
    operatorId: string | undefined,
  ): string | undefined => {
    const filterFn =
      operatorId === undefined
        ? (f: FilterOfValueType<string>) => f.definitionId === definitionId
        : (f: FilterOfValueType<string>) =>
            f.definitionId === definitionId && f.operatorId === operatorId;
    const filter = filters.find(filterFn) as
      | FilterOfValueType<string>
      | undefined;
    if (filter) {
      return filter.value;
    } else {
      return undefined;
    }
  };

  const asPct = (val: string | number | undefined): number | undefined => {
    if (val === undefined) return undefined;
    return Number(val) / 100;
  };

  let params = {
    organization_ids: organizationId === null ? undefined : organizationId,
    spacefunctions:
      spaceFunctionFilters.length > 0
        ? spaceFunctionFilters.flatMap((i) => i.value).join(',')
        : undefined,

    search: searchText || undefined,
    capacity__gt: filterToQuery('capacity', 'gt'),
    capacity__gte: filterToQuery('capacity', 'gte'),
    capacity__lt: filterToQuery('capacity', 'lt'),
    capacity__lte: filterToQuery('capacity', 'lte'),
    max_count_last_2_weeks__gt: filterToQuery('peak_count', 'gt'),
    max_count_last_2_weeks__gte: filterToQuery('peak_count', 'gte'),
    max_count_last_2_weeks__lt: filterToQuery('peak_count', 'lt'),
    max_count_last_2_weeks__lte: filterToQuery('peak_count', 'lte'),

    page: 1,
    page_size: PAGE_SIZE,
  };

  if (sort.column !== null && sort.direction !== 'none') {
    params['sort_column'] = sort.column;
    params['sort_direction'] = sort.direction;
  }
  return params;
}

// Fetch a list of all spaces and put them into the `ImplementedStore`
export async function fetchInitialSpaces(
  dispatch,
  jwtToken: string,
  filters: Filter[],
  sort: Sort,
  searchText: string,
  organizationId: Organization['id'] | null,
) {
  dispatch({ type: ImplementedActionTypes.IMPLEMENTED_LOAD_START });

  // Apply filters to the results that are returned.
  const params = generateParams(filters, searchText, sort, organizationId);

  let organizations: any, spaces: any;
  try {
    [organizations, spaces] = await Promise.all([
      SenseiApi.organizationList(jwtToken),
      SenseiApi.spaceList(jwtToken, params),
    ]);
  } catch (error) {
    dispatch({
      type: ImplementedActionTypes.IMPLEMENTED_LOAD_ERROR,
      error,
    });
    return;
  }

  dispatch({
    type: ImplementedActionTypes.IMPLEMENTED_LOAD_COMPLETE,
    organizations,
    total: spaces.total,
    spaces: spaces.results,
    filters,
    sort,
    searchText,
  });
}

export async function fetchNextPageOfSpaces(
  dispatch,
  jwtToken: string,
  filters: Filter[],
  sort: Sort,
  searchText: string,
  page: number,
  organizationId: Organization['id'] | null,
) {
  dispatch({ type: ImplementedActionTypes.IMPLEMENTED_NEXT_PAGE_LOAD_LOADING });

  // Apply filters to the results that are returned.
  const params = {
    ...generateParams(filters, searchText, sort, organizationId),
    page: page,
  };

  let spaces: any;
  try {
    spaces = await SenseiApi.spaceList(jwtToken, params);
  } catch (error) {
    dispatch({
      type: ImplementedActionTypes.IMPLEMENTED_NEXT_PAGE_LOAD_ERROR,
      error,
    });
    return;
  }

  dispatch({
    type: ImplementedActionTypes.IMPLEMENTED_NEXT_PAGE_LOAD_COMPLETE,
    spaces: spaces.results,
  });
}
